import { cn } from "@mabi-ui/utils";
import { ButtonBase, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CertiblokIcon from "../../../../utils/components/icons/CertiblokIcon";
import { useBetterMediaQuery } from "../../../../utils/hooks/useBetterMediaQuery";
import { DashboardCardProps } from "../SmallCardsGrid/DashboardSmallCard";

const DashboardBigCard = ({
	children,
	icon,
	onClick,
	isDisabled,
	href,
	color = "secondary",
	isNew,
	subtitle,
}: Omit<DashboardCardProps, "color" | "count" | "isCountLoading"> & {
	isDisabled?: boolean;
	color?: "secondary" | "yellow";
	isNew?: boolean;
	subtitle?: string;
}) => {
	const { t } = useTranslation();
	const isMd = useBetterMediaQuery("md");

	const [showAnimatedTooltip, setShowAnimatedTooltip] = useState(false);
	const [animationProgression, setAnimationProgression] = useState({ value: 0, direction: 1 });

	useEffect(() => {
		const editAnimation = () => {
			setAnimationProgression((prev) => {
				if (prev.value === 100) {
					return { value: 99, direction: -1 };
				}
				if (prev.value === 0) {
					return { value: 1, direction: 1 };
				}
				return { ...prev, value: prev.value + prev.direction * 1 };
			});
		};
		const int = setInterval(editAnimation, 15);

		return () => {
			clearInterval(int);
		};
	}, []);

	return (
		<ButtonBase
			onMouseEnter={() => setShowAnimatedTooltip(true)}
			onMouseLeave={() => setShowAnimatedTooltip(false)}
			className={cn(
				isDisabled
					? "bg-grey opacity-80 cursor-default hover:bg-[rgba(162,164,174,1)]"
					: color === "secondary"
					? "bg-secondary hover:bg-secondary/[0.92] active:bg-secondary/[0.86]"
					: "bg-alert hover:bg-alert/[0.92] active:bg-alert/[0.86]",
				"group relative transition-all flex-1 self-stretch p-3 md:p-4 flex gap-4 md:rounded-2xl rounded-xl text-left justify-start items-center"
			)}
			onClick={() => {
				onClick?.();
			}}
			href={isDisabled ? "" : href ?? ""}
			disableRipple
		>
			<div
				className={cn(
					"p-2 flex items-center justify-center rounded-xl bg-white/[0.24] flex-shrink-0",
					isDisabled && "group-hover:blur-sm"
				)}
			>
				<CertiblokIcon
					name={icon}
					className={cn("text-white", isDisabled && "group-hover:blur-[4px]")}
					size={isMd ? 40 : 32}
				/>
			</div>
			<div className="flex flex-col gap-1 flex-1">
				{typeof children === "string" ? (
					<Typography variant="subtitle1" className={cn("m-0 text-white", isDisabled && "group-hover:blur-[4px]")}>
						{children}
					</Typography>
				) : (
					children
				)}
				{subtitle !== undefined && (
					<Typography className={cn("text-white", isDisabled && "group-hover:blur-[4px]")} variant="caption">
						{subtitle}
					</Typography>
				)}
			</div>
			{!isDisabled && isNew && (
				<div className="bg-white p-[10px] rounded-3xl">
					<Typography
						component={"p"}
						className={cn("leading-[10px] h-[10px] font-bold", color === "secondary" ? "text-secondary" : "text-alert")}
						variant="overline"
					>
						{t("global.new")}
					</Typography>
				</div>
			)}
			{isDisabled && (
				<div
					className={cn(
						"w-full text-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity opacity-0 duration-500",
						showAnimatedTooltip && "opacity-100"
					)}
				>
					<Typography
						variant="button"
						className={cn("text-[24px] tracking-[3px] leading-6 m-0  text-white font-bold ")}
						sx={{
							background: `radial-gradient(circle at ${animationProgression.value}%, #d0d2d3, #ffffff)`,
							// #c8e8f0
							backgroundClip: "text",
							WebkitBackgroundClip: "text",
							WebkitTextFillColor: "transparent",
							/* animation: `${textEffect} 1s infinite`, */
						}}
					>
						{t("global.comingSoon")}
					</Typography>
				</div>
			)}
		</ButtonBase>
	);
};

export default DashboardBigCard;
